// @flow

import React from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import Ajv from "ajv";
import schema from "./step1-configure-schema";
import actionCreator from "@modules/service/actionCreator";
import { Button, SchemaForm } from "@components";
import type { StepProps } from "@components";
import type {
  NewShiftPattern,
  ShiftPattern,
  DraftShiftPattern,
  ShiftPatternDay,
} from "@modules/service/reducer/shift-patterns/types";
import * as serviceSelectors from "@modules/service/selectors";

type PropsFromState = $Exact<{
  draft: ?DraftShiftPattern,
  shiftPattern: ?ShiftPattern,
}>;

type PropsFromDispatch = $Exact<{
  setDraft: typeof actionCreator.UpdateShiftPatternDraft,
}>;

type Props = $Exact<{
  ...PropsFromState,
  ...PropsFromDispatch,
  ...StepProps,
  ...PropsFromLocalize,
}>;

const ORDERED_WEEKDAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
const SHIFT_DAYS_LENGTH = 7;

export class Step1Configure extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    // Class components needs constructor for accessing the SchemaForm's ref
    this.formRef = React.createRef();
  }
  validate: *;

  componentDidMount() {
    this.validate = new Ajv().compile(schema);
  }

  get isValid() {
    return this.validate ? this.validate(this.data) : false;
  }

  get currentData(): * {
    return this.props.draft || this.props.shiftPattern;
  }

  submitForm = () => {
    if (this.formRef) {
      this.formRef.current.submit();
    }
  };

  handleChange = (formData: Object) => {
    const currentData = this.currentData;

    this.props.setDraft({
      ...currentData,
      name: formData.name,
      days: formData.days,
      startDay: formData.startDay,
      shiftPatternDays: currentData.shiftPatternDays,
    });
  };

  handleSubmit = (formData: Object) => {
    if (this.props.handleSubmit) {
      const currentData = this.currentData;

      this.props.setDraft({
        ...currentData,
        name: formData.name,
        days: formData.days,
        startDay: formData.startDay,
        shiftPatternDays: this.getShiftPatternDays(formData),
      });

      this.props.handleSubmit({}, true);
    }
  };

  getShiftPatternDays = (formData: Object): Array<ShiftPatternDay> => {
    const currentData = this.currentData;
    const shiftPatternDays = currentData.shiftPatternDays;

    const desiredStartDay = formData.startDay;

    // Reorder the starting day
    const desiredStartDayIdx = shiftPatternDays.findIndex(rec => {
      return rec.day === desiredStartDay;
    });
    const beginningOftheNewArray = shiftPatternDays.slice(0, desiredStartDayIdx);
    const endOftheNewArray = shiftPatternDays.slice(desiredStartDayIdx, shiftPatternDays.length);
    let finalArray = endOftheNewArray.concat(beginningOftheNewArray);

    // Reducing the total days
    if (finalArray.length >= SHIFT_DAYS_LENGTH) {
      // Removing days
      finalArray = finalArray.slice(0, SHIFT_DAYS_LENGTH);
    } else {
      //Adding days
      const daysToAdd = SHIFT_DAYS_LENGTH - shiftPatternDays.length;
      for (let i = 0; i < daysToAdd; i++) {
        let targetedDayIdx = ORDERED_WEEKDAYS.findIndex(day => {
          if (finalArray.length > 0) {
            return finalArray[finalArray.length - 1].day === day;
          }
          return currentData.startDay === day;
        });

        // Added for the cases when we are creating new pattern and our first el needs to be the selected day,
        // otherwise needs to be next day so we are making +1
        if (finalArray.length > 0) targetedDayIdx++;
        if (targetedDayIdx >= ORDERED_WEEKDAYS.length) {
          targetedDayIdx = 0;
        }

        finalArray.splice(finalArray.length, 0, {
          isWorkingDay: true,
          shiftDurationSec: 0,
          day: ORDERED_WEEKDAYS[targetedDayIdx],
        });
      }
    }
    return finalArray;
  };

  get data() {
    const { draft, shiftPattern } = this.props;

    if (!draft && !shiftPattern) {
      return null;
    }

    const days = (draft || shiftPattern).shiftPatternDays || [];

    let startDay = days.length ? days[0].day : "MON";

    if ((draft || shiftPattern).startDay) {
      startDay = (draft || shiftPattern).startDay;
    }

    return {
      name: !draft ? shiftPattern.name : draft.name || null,
      days: SHIFT_DAYS_LENGTH,
      startDay,
    };
  }

  get uiSchema() {
    return {
      days: {
        "ui:title": "Days",
        "ui:widget": "number-input",
        "ui:options": {
          value: SHIFT_DAYS_LENGTH,
          editable: false,
          disabled: true,
        },
      },
    };
  }

  render() {
    if (!this.props.editable) {
      return null;
    }

    return (
      <React.Fragment>
        <div className="w-100 d-flex justify-content-center gs-container">
          {this.props.data ? (
            <SchemaForm
              ref={this.formRef}
              className="w-100"
              formData={this.data}
              schema={schema}
              uiSchema={this.uiSchema}
              onChange={this.handleChange}
              onSubmit={this.handleSubmit}
              externalSubmit
            >
              <div className="d-flex justify-content-center" />
            </SchemaForm>
          ) : null}
        </div>
        <div className="w-100 d-flex justify-content-end mt-5 mb-3">
          <Button materialIcon="fast_forward" disabled={!this.isValid} onClick={this.submitForm}>
            {this.props.t("NEXT")}
          </Button>
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state): PropsFromState => {
  return {
    draft: serviceSelectors.getShiftPatternDraft(state),
    shiftPattern: serviceSelectors.getCurrentShiftPattern(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  setDraft: actionCreator.UpdateShiftPatternDraft,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(Step1Configure);
